import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useActiveRightSidebarState,
	useRightSidebarController,
} from '@atlassian/jira-layout-controller';
import {
	PANEL_ID,
	PANEL_WIDTH,
} from '@atlassian/jira-onboarding-checklist-next/src/common/constants';
import {
	useJswOnboarding,
	useQuickstartUiStatus,
} from '@atlassian/jira-onboarding-quickstart-core';
import { QUICKSTART_UI_STATUS } from '@atlassian/jira-onboarding-quickstart-core/src/services';
import { fireAnalyticsOnOpenRightHandSidebar } from '@atlassian/jira-onboarding-quickstart-core/src/utils/analytics';
import type { TriggersToOpenChecklist } from '../../common/types';

// We tend to combine flows of openChecklist from both minimised and dismissed

type OpenChecklistProps = {
	trigger: TriggersToOpenChecklist;
	openTask?: string;
};

export const useOpenChecklist = () => {
	const [
		{ isOpen, isDismissed, openTask: currentOpenTask },
		{ nextSetChecklistIsOpen, nextSetChecklistIsDismissed, nextOpenTask },
	] = useJswOnboarding();
	const { openRightSidebar } = useRightSidebarController();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [quickstartStatus] = useQuickstartUiStatus();
	const currentRightSidebarState = useActiveRightSidebarState();

	const currentPanelId = currentRightSidebarState?.panelId;

	const openChecklist = ({ trigger, openTask }: OpenChecklistProps) => {
		if (openTask && openTask !== currentOpenTask) {
			nextOpenTask(openTask);
		}

		// This early return is problematic. `openRightSidebar` does several things this early return does not account for.
		// 1. It sets the panel width in the 2nd arg. It can be 0. No check is done on the panel width before returning.
		// 2. It sets the QS panel to the top of the stack in the 4th arg. No check is done on where in the stack the QS actually is before returning.
		// Thus, after kuro-4953-quickstart-improvements-and-fixes rolls out, delete the block below.
		if (!fg('kuro-4953-quickstart-improvements-and-fixes')) {
			if (
				currentPanelId === PANEL_ID &&
				expValEquals('jsm_business_quickstart', 'cohort', 'experiment')
					? quickstartStatus === QUICKSTART_UI_STATUS.OPEN
					: isOpen
			) {
				return;
			}
		}

		openRightSidebar(PANEL_ID, PANEL_WIDTH, false, true);

		fireAnalyticsOnOpenRightHandSidebar(createAnalyticsEvent, {
			quickstartStatus,
			currentRightSidebarPanelId: currentPanelId || '',
			openRightSidebarToTop: true,
			trigger,
		});
		if (
			expValEquals('jsm_business_quickstart', 'cohort', 'experiment')
				? quickstartStatus === QUICKSTART_UI_STATUS.DISMISSED
				: isDismissed
		) {
			nextSetChecklistIsDismissed(false);
		} else {
			nextSetChecklistIsOpen(true);
		}
	};

	return openChecklist;
};
